/**
 * Pure derivation of the analytics super-property bag (PIX-553).
 *
 * Given the user payload, active board, and environment, produce the context
 * attached to every event. Kept side-effect free and framework-free so it can
 * be unit tested with `node --test` (see tests/eventProperties.test.js) -
 * same split as errorFilters.js, and for the same reason: this is where a
 * silent error would do the most damage.
 *
 * "Silent" is literal. If `days_since_trial_start` is off by one, nothing
 * throws and no request fails - the activation and free-to-paid numbers are
 * simply wrong, on a dashboard someone is about to make a decision from.
 *
 * PRIVACY: the user payload carries `email`, `full_name`, and `avatar`. None
 * of them may appear in the output. Only the opaque internal uuid plus
 * non-identifying context leaves the browser.
 */
import { PROPS, SUBSCRIPTION_STATUS, SURFACES } from './events.js';

const MS_PER_DAY = 86400000;

/**
 * Parse a backend date (`YYYY-MM-DD`, from Python's `date.isoformat()`) to a
 * UTC midnight timestamp. Returns null for anything unparseable.
 */
function parseDateToUtc(value) {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const stamp = Date.UTC(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(stamp) ? null : stamp;
}

/**
 * The caller's current calendar day as a UTC midnight timestamp. Uses local
 * date components deliberately: both sides of the subtraction are then plain
 * calendar dates, which is what "day 3 of the trial" means to a student. A
 * student mid-flight across timezones can be a day out; that is acceptable
 * for analytics and cheaper than shipping a timezone library.
 */
function startOfDayUtc(now) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Whole days elapsed from a backend date to now. Day of start is 0.
 * Returns null when either side is unknown.
 */
export function daysSince(startDate, now = new Date()) {
  const start = parseDateToUtc(startDate);
  const today = startOfDayUtc(now);
  if (start === null || today === null) return null;
  return Math.round((today - start) / MS_PER_DAY);
}

/**
 * Which bucket the user is in for monetisation breakdowns.
 *
 * Order matters: the backend's `is_subscribed` is true during a trial as
 * well (an active trial IS an active subscription server-side), so trial has
 * to be tested first or every trialling student would be reported as paid.
 */
export function deriveSubscriptionStatus(user) {
  if (!user) return SUBSCRIPTION_STATUS.ANONYMOUS;
  if (user.is_trial) return SUBSCRIPTION_STATUS.TRIAL;
  if (user.is_subscribed) return SUBSCRIPTION_STATUS.PAID;
  return SUBSCRIPTION_STATUS.FREE;
}

/**
 * An empty string means "no value", not a value (PIX-629).
 *
 * `activeBoardName` is a computed ending `?? ''`, which is correct for a UI
 * binding and wrong for an event property: PostHog treats `''` and `null` as
 * distinct, so the student surface grew an empty-string bucket sitting beside
 * the organisation surface's null one, both meaning "no board". Normalising
 * here rather than in the store keeps the empty string for the templates that
 * render it.
 *
 * Note `?? null` does NOT do this - it only catches `undefined`, and
 * `'' ?? null` is `''`. That is precisely how this slipped through.
 */
export function absentAsNull(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  return value;
}

/**
 * Board context for the surface the user is currently on (PIX-604).
 *
 * Pure so the null-out is covered by a test rather than trusted to wiring.
 * That matters more here than elsewhere: this is the live defect PIX-604 fixes,
 * and its failure mode is the one this module exists to prevent - the board is
 * simply wrong, nothing throws, and every "worksheet building by board"
 * breakdown is quietly attributed to whatever the teacher last revised.
 *
 * The student board is meaningless in the organisation panel, and super
 * properties persist in browser storage, so it has to be actively cleared
 * rather than merely not set.
 */
export function deriveBoardContext({
  surface = null,
  uuid = null,
  name = null,
} = {}) {
  const onPanel = surface === SURFACES.ORGANISATION;
  return {
    [PROPS.BOARD]: onPanel ? null : absentAsNull(uuid),
    [PROPS.BOARD_NAME]: onPanel ? null : absentAsNull(name),
  };
}

/**
 * The organisation to attribute a STUDENT's activity to, or null.
 *
 * Takes the first membership, which is correct here and only here: a student
 * belongs to one school, and the list shape is an artefact of the payload
 * rather than a real plurality. Panel users are the opposite case - they may
 * hold roles at several schools, with a different role at each - so the panel
 * keys off the SELECTED organisation instead. See
 * orgEventProperties.deriveSelectedOrganisation, and do not reuse this for it.
 */
export function deriveOrganisation(user) {
  const organisations = Array.isArray(user?.organisations)
    ? user.organisations
    : [];
  if (organisations.length) return organisations[0];
  return user?.organisation ?? null;
}

/**
 * Build the super-property bag. Every value here is non-identifying.
 *
 * @param {object}  input
 * @param {object?} input.user             user-info payload, or null when anonymous
 * @param {string?} input.activeBoardUuid  currently selected board
 * @param {string?} input.activeBoardName  its display name
 * @param {string?} input.environment      deploy environment label
 * @param {Date?}   input.now              injected clock, for tests
 */
export function deriveSuperProperties({
  user = null,
  activeBoardUuid = null,
  activeBoardName = null,
  environment = null,
  now = new Date(),
} = {}) {
  const organisation = deriveOrganisation(user);
  const isTrial = Boolean(user?.is_trial);

  return {
    // Separates a prospective student from a signed-in one browsing the
    // blog - without it, returning students inflate the top of the funnel.
    [PROPS.IS_AUTHENTICATED]: Boolean(user),
    [PROPS.BOARD]: activeBoardUuid ?? null,
    [PROPS.BOARD_NAME]: activeBoardName ?? null,
    [PROPS.SUBSCRIPTION_STATUS]: deriveSubscriptionStatus(user),
    [PROPS.IS_TRIAL]: isTrial,
    // Only meaningful while a trial is running; null otherwise so a stale
    // countdown can't be read as "this paid user has 0 days left".
    [PROPS.DAYS_LEFT_IN_TRIAL]: isTrial
      ? (user?.trial_days_remaining ?? null)
      : null,
    // Kept for anyone who has ever trialled, not just current trials - it is
    // what makes "bought on day 3 vs day 9" answerable after the fact.
    [PROPS.DAYS_SINCE_TRIAL_START]: daysSince(user?.trial_start_date, now),
    [PROPS.HAS_BUNDLE]: Boolean(user?.has_bundle),
    [PROPS.IS_ORGANISATION_USER]: Boolean(organisation),
    [PROPS.ORGANISATION_ROLE]: organisation?.role ?? null,
    [PROPS.USER_ROLE]: user?.role ?? null,
    [PROPS.APP_ENVIRONMENT]: environment ?? null,
  };
}

/**
 * The `topic` property for a question event: its first topic's uuid.
 *
 * Question payloads carry `topics: [{uuid, name}]` (one level since PIX-773);
 * the property used to read a `topic_uuid` field the API never sends, so it
 * was always null.
 */
export function questionTopicUuid(question) {
  return question?.topics?.[0]?.uuid ?? null;
}
